import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { stCards, stFade, stPanels } from './strength.animations';

export type StTone = 'pink' | 'teal' | 'amber' | 'blue' | 'magenta' | 'violet';

export interface StPillar {
  id: string;
  text: string;
  tone: StTone;
  icon: 'users' | 'expert' | 'shield' | 'agile' | 'code' | 'award';
}

export interface StPanel {
  id: 'vision' | 'mission';
  label: string;
  accent: string;
  tone: Extract<StTone, 'pink' | 'violet'>;
  body: string;
}

@Component({
  selector: 'app-strength',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './strength.component.html',
  styleUrls: ['./strength.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [stFade, stCards, stPanels],
})
export class StrengthComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stRoot') stRoot?: ElementRef<HTMLElement>;
  @ViewChild('stVm') stVm?: ElementRef<HTMLElement>;

  /** Flips once when the strength hero scrolls into view. */
  readonly heroState = signal<'hidden' | 'shown'>('hidden');
  /** Flips once when the vision/mission row scrolls into view. */
  readonly vmState = signal<'hidden' | 'shown'>('hidden');

  /** The six pillars, in the order the reference layout reads them. */
  readonly pillars: StPillar[] = [
    {
      id: 'people',
      text: 'Empowering skilled personnel to drive innovation',
      tone: 'pink',
      icon: 'users',
    },
    {
      id: 'experience',
      text: 'Experienced team of software development professionals',
      tone: 'teal',
      icon: 'expert',
    },
    {
      id: 'trust',
      text: 'Transparency and reliability in our work culture procedures',
      tone: 'amber',
      icon: 'shield',
    },
    {
      id: 'agile',
      text: 'Agile methodology to adapt to changing requirements',
      tone: 'blue',
      icon: 'agile',
    },
    {
      id: 'stack',
      text: 'Custom programming skills in .NET, XML, and more',
      tone: 'magenta',
      icon: 'code',
    },
    {
      id: 'quality',
      text: 'Consistent delivery of high-quality solutions',
      tone: 'violet',
      icon: 'award',
    },
  ];

  readonly panels: StPanel[] = [
    {
      id: 'vision',
      label: 'Our',
      accent: 'Vision',
      tone: 'pink',
      body:
        'To empower businesses and individuals through innovative technology ' +
        'solutions, fostering resilience and growth in an ever-evolving digital ' +
        'landscape. We envision a future where technology seamlessly integrates ' +
        'with human ingenuity, enabling organizations to thrive and make a ' +
        'meaningful impact.',
    },
    {
      id: 'mission',
      label: 'Our',
      accent: 'Mission',
      tone: 'violet',
      body:
        'To design, develop, and deliver exceptional software and web solutions ' +
        'that meet the unique needs of our clients, exceeding their expectations ' +
        'and driving their success. Delivering top-notch solutions that meet the ' +
        'highest standards of quality and innovation.',
    },
  ];

  private observers: IntersectionObserver[] = [];
  private reduced = false;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Settle this before the first render so reduced-motion visitors never see
    // the hidden state animate away.
    if (!isPlatformBrowser(platformId)) return;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) {
      this.heroState.set('shown');
      this.vmState.set('shown');
    }
  }

  trackById(_i: number, item: { id: string }): string {
    return item.id;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // SSR / prerender: ship the finished state so the markup is complete.
      this.heroState.set('shown');
      this.vmState.set('shown');
      return;
    }

    this.watch(this.stRoot, () => this.heroState.set('shown'));
    this.watch(this.stVm, () => this.vmState.set('shown'));
  }

  ngOnDestroy(): void {
    this.observers.forEach((io) => io.disconnect());
    this.observers = [];
  }

  /** Run `onIn` once the element is in view — immediately if it already is. */
  private watch(ref: ElementRef<HTMLElement> | undefined, onIn: () => void): void {
    const el = ref?.nativeElement;
    if (!el) return;

    if (this.reduced || el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      onIn();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.zone.run(onIn);
        io.disconnect();
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    this.zone.runOutsideAngular(() => io.observe(el));
    this.observers.push(io);
  }
}
